import { useAuth } from 'react-oidc-context';
import { useTenant } from '@parcel2go/npm-p2g-app';
import { UnauthorisedResponse } from '~/lib/api/unauthorised-response';
import { getApiPath } from './get-api-path';
import { ApiResponse } from '../api-response';
import { authedFetch } from '../authed-fetch';
import { parseResult } from '../parse-result';
import {
  ActionType,
  GetInstructionsApiResponseModel,
  Integration,
  IntegrationType,
  LinkAccountResponseApiModel,
  SettingsApiResponseModel,
  StrVal
} from './types';
import { GetBaseUrl } from '~/apps/account-linking/lib/helpers/GetBaseUrl';
import { useCallback, useRef } from 'react';

export interface IApiResponse<T> {
  success: boolean;
  result: T | null;
}

export function useAccountLinkingApi() {
  const { user } = useAuth();
  const { tenant } = useTenant();

  // Hold the live access token in a ref so each request always sends the current
  // token, while the callbacks below keep a stable identity across silent token
  // renewals. We key the callbacks on `isAuthenticated` (a boolean that only flips
  // on login/logout) rather than the token string itself, which changes on every
  // renewal. Capturing the token string in the dependency arrays caused consumers
  // (e.g. the KYC document-upload form) to re-fetch and reset on every renewal or
  // window-focus revalidation.
  const tokenRef = useRef(user?.access_token);
  tokenRef.current = user?.access_token;
  const isAuthenticated = !!user?.access_token;

  const getintegrationsummary = useCallback(
    async (type?: IntegrationType): Promise<ApiResponse<Integration[]>> => {
      if (!tokenRef.current) {
        return UnauthorisedResponse;
      }

      const url = getApiPath(tenant, type ? `integrations?type=${type}` : `integrations`);
      const response = await authedFetch(tenant, url, { method: 'GET' }, tokenRef.current);
      const result = await parseResult<Integration[]>(response);

      // The API should do this but just in case, we filter by type here as well to avoid any issues with the UI if the API returns incorrect data.
      if (type && result.success && result.result) {
        result.result = result.result.filter((x) => x.type === type);
      }

      return result;
    },
    [isAuthenticated, tenant]
  );

  const postInternalAuthCallbackUrl = useCallback(
    async (internalAuthCallbackUrl: string): Promise<ApiResponse<{ redirectUrl: string }>> => {
      const response = await authedFetch(
        tenant,
        internalAuthCallbackUrl,
        {
          method: 'POST',
          body: JSON.stringify({
            ReturnUrl: GetBaseUrl()
          })
        },
        tokenRef.current
      );
      return parseResult<{ redirectUrl: string }>(response);
    },
    [isAuthenticated, tenant]
  );

  const getActionInstructions = useCallback(
    async (
      integrationsType: string,
      slug: string,
      action: ActionType,
      accountId?: string
    ): Promise<ApiResponse<GetInstructionsApiResponseModel>> => {
      if (!tokenRef.current) {
        return UnauthorisedResponse;
      }

      const url = accountId
        ? getApiPath(tenant, `integrations/${integrationsType}/${slug}/${action}/instructions/${accountId}`)
        : getApiPath(tenant, `integrations/${integrationsType}/${slug}/${action}/instructions`);

      const response = await authedFetch(tenant, url, { method: 'GET' }, tokenRef.current);

      return parseResult<GetInstructionsApiResponseModel>(response);
    },
    [isAuthenticated, tenant]
  );

  const getSettings = useCallback(async (): Promise<ApiResponse<SettingsApiResponseModel>> => {
    if (!tokenRef.current) {
      return UnauthorisedResponse;
    }

    try {
      const url = getApiPath(tenant, `setup`);
      const response = await authedFetch(tenant, url, {}, tokenRef.current);
      return parseResult<SettingsApiResponseModel>(response);
    } catch {
      return { success: false } as ApiResponse<any>;
    }
  }, [isAuthenticated, tenant]);

  const postActionValues = useCallback(
    async (
      integrationsType: string,
      slug: string,
      action: ActionType,
      model: StrVal,
      accountId?: string
    ): Promise<ApiResponse<LinkAccountResponseApiModel>> => {
      // Add return URL for Links and Renews
      if (action == 'Link' || action == 'Renew') {
        model['ReturnUrl'] = GetBaseUrl();
      }

      // If there's a referrer stored in local storage check if it matches the current integration and action, if so add it to the values - filtering to allowed actions will happen in edge
      const referrerId =
        localStorage.getItem(`accountLinking_referrer_${slug}_${action}`.toLowerCase()) ??
        localStorage.getItem(`accountLinking_referrer_global_${action}`.toLowerCase()) ??
        localStorage.getItem(`accountLinking_referrer_global`.toLowerCase());
      if (referrerId) {
        model['ReferrerId'] = referrerId;
      }

      const url = accountId
        ? getApiPath(tenant, `integrations/${integrationsType}/${slug}/${action}/${accountId}`)
        : getApiPath(tenant, `integrations/${integrationsType}/${slug}/${action}`);
      const response = await authedFetch(
        tenant,
        url,
        { method: 'POST', body: JSON.stringify(model) },
        tokenRef.current
      );
      return parseResult<LinkAccountResponseApiModel>(response);
    },
    [isAuthenticated, tenant]
  );

  const getMarketplaceRedirectUrl = useCallback(
    async (slug: string) => {
      const sanitisedSlug = slug.toLowerCase();
      const action = sanitisedSlug === 'ebay' ? 'Link' : 'Install';
      const res = await postActionValues('Marketplace', sanitisedSlug, action, {});
      if (res.success && res.result.redirectUrl) {
        return res.result.redirectUrl;
      }
      return '';
    },
    [postActionValues]
  );

  return {
    getintegrationsummary,
    postInternalAuthCallbackUrl,
    getActionInstructions,
    getSettings,
    postActionValues,
    getMarketplaceRedirectUrl
  } as const;
}
